import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Chain } from 'viem';

import { useSwitchChainHandler } from 'modules/Room/Hooks/useSwitchHandler';

import Dropdown from 'shared/components/ui/DropDown/DropDown';
import { chainIcons } from 'shared/config';
import { ROUTES } from 'shared/constants';
import { useChainId, useSwitchChain } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';

import { EXCHANGES } from './empty-wallet-section.constants';

export const EmptyWalletSection: FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const chainId = useChainId();
  const exchanges = EXCHANGES(chainId);

  const { chains } = useSwitchChain();
  const switchChainHandler = useSwitchChainHandler();
  const location = useLocation();
  const navigate = useNavigate();

  const [networkOpen, setNetworkOpen] = useState(false);

  const chainsList = useMemo(
    () =>
      chains.map((chain: Chain) => ({
        label: chain.name,
        value: chain.id,
        tooltip: `chains.${chain.id}`,
        icon: chainIcons?.[chain.id],
      })),
    [chains],
  );

  return (
    <div className="relative flex flex-col gap-3 pb-6 pt-4 text-md font-normal leading-4 text-white sm:gap-6 sm:p-6 sm:pt-0 sm:text-base sm:leading-5">
      <h2 className="text-base font-bold leading-4 text-red sm:text-[24px] sm:leading-8">
        {t('lobby.emptyWallet.title')}
      </h2>
      <div>
        <span>{t('lobby.emptyWallet.description')}</span>
        <span className="relative">
          <span
            onClick={() => setNetworkOpen(!networkOpen)}
            className="inline-block cursor-pointer text-xs font-semibold text-yellow-text transition-opacity hover:opacity-80 sm:ml-2 sm:inline sm:text-base"
          >
            {t('lobby.emptyWallet.switchNetwork')}
          </span>

          <Dropdown
            open={networkOpen}
            list={chainsList}
            onClick={async (value) => {
              await switchChainHandler(value);
              setNetworkOpen(false);
              if (location.pathname.startsWith(ROUTES.ROOM)) {
                navigate(ROUTES.LOBBY);
              }
            }}
            active={chainId}
            position={{ top: 25, left: isMobile ? 0 : -55 }}
            shouldShowTooltip={false}
          />
        </span>
      </div>

      <p>{t('lobby.emptyWallet.instructions')}</p>
      <hr className="border-indigo" />
      <div className="flex items-center justify-between gap-2">
        {exchanges.map((exchange) => (
          <a
            key={exchange.id}
            href={exchange.url}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform hover:scale-105 [&>svg]:h-auto [&>svg]:max-h-5 [&>svg]:w-full sm:[&>svg]:max-h-none"
          >
            {exchange.logo}
          </a>
        ))}
      </div>
      <hr className="border-indigo" />
      <p className="text-light-grape">{t('lobby.emptyWallet.footer')}</p>
    </div>
  );
};
